"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Step = {
  num: string;
  title: string;
  body: string;
  icon?: ReactNode;
  accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson";
};

const ACCENT: Record<NonNullable<Step["accent"]>, { bg: string; text: string; ring: string; line: string }> = {
  red: { bg: "from-red-500/20 to-red-500/0", text: "text-red-400", ring: "ring-red-500/40", line: "from-red-500 to-red-500/0" },
  coral: { bg: "from-coral-500/20 to-coral-500/0", text: "text-coral-400", ring: "ring-coral-500/40", line: "from-coral-500 to-coral-500/0" },
  purple: { bg: "from-purple-500/20 to-purple-500/0", text: "text-purple-400", ring: "ring-purple-500/40", line: "from-purple-500 to-purple-500/0" },
  cyan: { bg: "from-cyan-500/20 to-cyan-500/0", text: "text-cyan-400", ring: "ring-cyan-500/40", line: "from-cyan-500 to-cyan-500/0" },
  denim: { bg: "from-denim-500/20 to-denim-500/0", text: "text-denim-400", ring: "ring-denim-500/40", line: "from-denim-500 to-denim-500/0" },
  emerald: { bg: "from-emerald-500/20 to-emerald-500/0", text: "text-emerald-400", ring: "ring-emerald-500/40", line: "from-emerald-500 to-emerald-500/0" },
  lime: { bg: "from-lime-500/20 to-lime-500/0", text: "text-lime-400", ring: "ring-lime-500/40", line: "from-lime-500 to-lime-500/0" },
  "amber-gold": { bg: "from-amber-gold-500/20 to-amber-gold-500/0", text: "text-amber-gold-400", ring: "ring-amber-gold-500/40", line: "from-amber-gold-500 to-amber-gold-500/0" },
  crimson: { bg: "from-crimson-500/20 to-crimson-500/0", text: "text-crimson-400", ring: "ring-crimson-500/40", line: "from-crimson-500 to-crimson-500/0" },
};

export function StepperBlock({ steps, orientation = "vertical", title, eyebrow, description }: { steps: Step[]; orientation?: "vertical" | "horizontal"; title?: string; eyebrow?: string; description?: string }) {
  return (
    <div className="space-y-12">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      {orientation === "vertical" ? (
        <div className="relative">
          {steps.map((step, i) => {
            const accent = step.accent ?? "purple";
            const a = ACCENT[accent];
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative grid grid-cols-[auto_1fr] gap-6 pb-12 last:pb-0">
                <div className="relative flex flex-col items-center">
                  <div className={cn("relative flex h-14 w-14 items-center justify-center rounded-full bg-muted font-rajdhani text-xl font-bold ring-2", a.ring, a.text)}>
                    {step.num || i + 1}
                    <div className={cn("absolute inset-0 -z-10 rounded-full bg-gradient-to-br opacity-50 blur-xl", a.bg)} />
                  </div>
                  {i < steps.length - 1 && <div className={cn("mt-2 h-full w-px flex-1 bg-gradient-to-b", a.line)} />}
                </div>
                <div className="pt-2">
                  <div className={cn("text-xs font-bold uppercase tracking-[0.2em]", a.text)}>Stage {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-1 font-rajdhani text-2xl font-bold text-foreground">{step.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-foreground/80/85">{step.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => {
            const accent = step.accent ?? "purple";
            const a = ACCENT[accent];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className={cn("glass-card relative overflow-hidden rounded-2xl border border-border p-6", `ring-1 ${a.ring}`)}>
                <div className={cn("absolute inset-0 bg-gradient-to-br", a.bg)} />
                <div className="relative">
                  <div className={cn("mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-muted font-rajdhani text-xl font-bold ring-2", a.ring, a.text)}>{step.num || i + 1}</div>
                  <h3 className="font-rajdhani text-xl font-bold text-foreground">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/80/80">{step.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
